import { create } from 'zustand';

/**
 * Shape of trades coming from MEIC WS:
 *
 * trade_opened:
 * {
 *   id: string,
 *   optionType: 'CALL' | 'PUT',
 *   shortStrike: number,
 *   longStrike: number,
 *   width: number,
 *   premium: number,
 *   entryTimestampEt: string | null,
 *   spxAtEntry: number | null,
 *   status: 'OPEN'
 * }
 *
 * trade_update (OPEN tick):
 * {
 *   id: string,
 *   optionType: 'CALL' | 'PUT',
 *   shortStrike: number,
 *   longStrike: number,
 *   premium: number,
 *   spreadMark: number,
 *   currentPnlDollars: number,
 *   realizedPnlDollars: null,
 *   stopLevel: number,
 *   spxStopEstimateLinear: number | null,
 *   spxLevel: number,
 *   etTimeStr: string,
 *   status: 'OPEN'
 * }
 *
 * trade_update (STOP):
 * {
 *   ...,
 *   currentPnlDollars: realizedPnlDollars,
 *   realizedPnlDollars: number,
 *   status: 'CLOSED',
 *   reason: 'STOP',
 *   outcome: 'STOP'
 * }
 *
 * trade_update (EXPIRE):
 * {
 *   ...,
 *   spreadMark: 0,
 *   currentPnlDollars: realizedPnlDollars,
 *   realizedPnlDollars: number,
 *   stopLevel: premium * 2,
 *   spxStopEstimateLinear: null,
 *   etTimeStr: null,
 *   status: 'CLOSED',
 *   reason: 'EXPIRE',
 *   outcome: 'EXPIRE'
 * }
 *
 * daily_summary:
 * {
 *   date: string,
 *   totalRealizedPnlDollars: number,
 *   totalTradesClosed: number,
 *   numWins: number,
 *   numLosses: number,
 *   numStops: number,
 *   numExpires: number,
 *   avgWinPnlDollars: number,
 *   avgLossPnlDollars: number
 * }
 *
 * market_state (from getCurrentMarketState):
 * {
 *   date,
 *   msGex,
 *   vvixFastZ,
 *   vvixSlowZ,
 *   regime,
 *   gexSign,
 *   volTrend
 * }
 */

const useMeicWebsocketDataStore = create((set, get) => ({
  /**
   * Map of trades keyed by id (string).
   * Each value is the merged shape of trade_opened + trade_update payloads.
   */
  tradesById: {},

  /**
   * Latest MEIC EOD daily summary from the WS daily_summary message.
   */
  dailySummary: null,

  /**
   * Latest market state from MEIC WS (or null if none yet).
   * {
   *   date,
   *   msGex,
   *   vvixFastZ,
   *   vvixSlowZ,
   *   regime,
   *   gexSign,
   *   volTrend
   * }
   */
  marketState: null,

  /**
   * Convenience field for current regime (0–3).
   */
  currentRegime: null,

  /**
   * Connection status (used by the client for UI/debug).
   * 'disconnected' | 'connecting' | 'connected' | 'auth_error'
   */
  connectionStatus: 'disconnected',

  /**
   * Snapshot of today's trades loaded from DB via `meic_initial_state`.
   */
  dailyTradesDate: null,
  dailyTrades: [],

  /**
   * Upsert a trade from either trade_opened or trade_update.
   * New fields overwrite existing; missing fields are preserved.
   */
  upsertTrade: incomingTrade => {
    if (!incomingTrade || !incomingTrade.id) {
      console.warn('[MEIC Store] upsertTrade called without valid id:', incomingTrade);
      return;
    }

    const id = String(incomingTrade.id);

    set(state => {
      const prev = state.tradesById[id] || {};

      const merged = {
        ...prev,
        ...incomingTrade,
      };

      return {
        tradesById: {
          ...state.tradesById,
          [id]: merged,
        },
      };
    });
  },

  /**
   * Seed today's trades snapshot from the 'meic_initial_state' WS message.
   */
  setInitialTrades: ({ tradeDate, trades }) => {
    if (!Array.isArray(trades)) {
      console.warn('[MEIC Store] setInitialTrades called without valid trades array:', trades);
      return;
    }

    // console.log('[MEIC Store] setInitialTrades snapshot:', {
    //   tradeDate,
    //   count: trades.length,
    //   sample: trades[0] || null,
    // });

    set({
      dailyTradesDate: tradeDate || null,
      dailyTrades: trades,
    });
  },

  /**
   * Store the official MEIC EOD summary from the 'daily_summary' WS message.
   */
  setDailySummary: summary => {
    if (!summary) {
      console.warn('[MEIC Store] setDailySummary called without summary:', summary);
      return;
    }

    console.log('[MEIC Store] daily_summary received:', summary);

    set({
      dailySummary: summary,
    });
  },

  /**
   * Optional helper to clear the daily summary.
   */
  resetDailySummary: () => {
    set({ dailySummary: null });
  },

  /**
   * Set market state from the 'market_state' WS message.
   * Keeps marketState and currentRegime in sync.
   */
  setMarketState: state => {
    if (!state || typeof state.regime === 'undefined') {
      console.warn('[MEIC Store] setMarketState called without valid state:', state);
      return;
    }

    set({
      marketState: state,
      currentRegime: state.regime,
    });
  },

  /**
   * Optional manual override for regime.
   */
  setRegime: regime => {
    set(prev => ({
      currentRegime: regime,
      marketState: prev.marketState
        ? { ...prev.marketState, regime }
        : { regime },
    }));
  },

  /**
   * Set connection status.
   */
  setConnectionStatus: status => {
    set({ connectionStatus: status });
  },

  /**
   * Clear all trades.
   */
  clearTrades: () => {
    set({ tradesById: {} });
  },

  /**
   * Convenience selectors
   */
  getAllTrades: () => {
    const { tradesById } = get();
    return Object.values(tradesById);
  },

  getOpenTrades: () => {
    const { tradesById } = get();
    return Object.values(tradesById).filter(t => t.status === 'OPEN');
  },

  getClosedTrades: () => {
    const { tradesById } = get();
    return Object.values(tradesById).filter(t => t.status === 'CLOSED');
  },

  getCurrentRegime: () => {
    const { currentRegime } = get();
    return currentRegime;
  },

  getMarketState: () => {
    const { marketState } = get();
    return marketState;
  },

  getDailyTrades: () => {
    const { dailyTrades } = get();
    return dailyTrades;
  },

  getDailyTradesDate: () => {
    const { dailyTradesDate } = get();
    return dailyTradesDate;
  },

  getDailySummary: () => {
    const { dailySummary } = get();
    return dailySummary;
  },
}));

export default useMeicWebsocketDataStore;




// import { create } from 'zustand';
//
// /**
//  * Shape of trades coming from MEIC WS:
//  *
//  * trade_opened:
//  * {
//  *   id: string,
//  *   optionType: 'CALL' | 'PUT',
//  *   shortStrike: number,
//  *   longStrike: number,
//  *   width: number,
//  *   premium: number,
//  *   entryTimestampEt: string | null,
//  *   spxAtEntry: number | null,
//  *   status: 'OPEN'
//  * }
//  *
//  * trade_update (OPEN tick):
//  * {
//  *   id: string,
//  *   optionType: 'CALL' | 'PUT',
//  *   shortStrike: number,
//  *   longStrike: number,
//  *   premium: number,
//  *   spreadMark: number,
//  *   currentPnlDollars: number,
//  *   realizedPnlDollars: null,
//  *   stopLevel: number,
//  *   spxStopEstimateLinear: number | null,
//  *   spxLevel: number,
//  *   etTimeStr: string,
//  *   status: 'OPEN'
//  * }
//  *
//  * trade_update (STOP):
//  * {
//  *   ...,
//  *   currentPnlDollars: realizedPnlDollars,
//  *   realizedPnlDollars: number,
//  *   status: 'CLOSED',
//  *   reason: 'STOP',
//  *   outcome: 'STOP'
//  * }
//  *
//  * trade_update (EXPIRE):
//  * {
//  *   ...,
//  *   spreadMark: 0,
//  *   currentPnlDollars: realizedPnlDollars,
//  *   realizedPnlDollars: number,
//  *   stopLevel: premium * 2,
//  *   spxStopEstimateLinear: null,
//  *   etTimeStr: null,
//  *   status: 'CLOSED',
//  *   reason: 'EXPIRE',
//  *   outcome: 'EXPIRE'
//  * }
//  *
//  // * daily_summary:
//  // * {
//  // *   date: string,
//  // *   totalRealizedPnlDollars: number,
//  // *   totalTradesClosed: number,
//  // *   numWins: number,
//  // *   numLosses: number,
//  // *   numStops: number,
//  // *   numExpires: number,
//  // *   avgWinPnlDollars: number,
//  // *   avgLossPnlDollars: number
//  // * }
//  *
//  * market_state (from getCurrentMarketState):
//  * {
//  *   date,
//  *   msGex,
//  *   vvixFastZ,
//  *   vvixSlowZ,
//  *   regime,
//  *   gexSign,
//  *   volTrend
//  * }
//  */
//
// const useMeicWebsocketDataStore = create((set, get) => ({
//   /**
//    * Map of trades keyed by id (string).
//    * Each value is the merged shape of trade_opened + trade_update payloads.
//    */
//   tradesById: {},
//
//   // /**
//   //  * Latest daily summary from MEIC WS (or null if none yet).
//   //  */
//   // dailySummary: null,
//
//   /**
//    * Latest market state from MEIC WS (or null if none yet).
//    * {
//    *   date,
//    *   msGex,
//    *   vvixFastZ,
//    *   vvixSlowZ,
//    *   regime,
//    *   gexSign,
//    *   volTrend
//    * }
//    */
//   marketState: null,
//
//   /**
//    * Convenience field for current regime (0–3).
//    */
//   currentRegime: null,
//
//   /**
//    * Connection status (used by the client for UI/debug).
//    * 'disconnected' | 'connecting' | 'connected' | 'auth_error'
//    */
//   connectionStatus: 'disconnected',
//
//   /**
//    * 🔹 NEW:
//    * Snapshot of today's trades loaded from DB via `meic_initial_state`.
//    * These rows come directly from `getTodayMeicTrades`:
//    * [
//    *   {
//    *     entry_timestamp_et,
//    *     stop_loss_timestamp_et,
//    *     option_type,
//    *     short_strike,
//    *     long_strike,
//    *     premium,
//    *     outcome,
//    *     profit_loss,
//    *     net_profit_loss
//    *   },
//    *   ...
//    * ]
//    */
//   dailyTradesDate: null, // 'YYYY-MM-DD' in ET
//   dailyTrades: [],       // raw snapshot rows from DB
//
//   /**
//    * Upsert a trade from either trade_opened or trade_update.
//    * New fields overwrite existing; missing fields are preserved.
//    */
//   upsertTrade: (incomingTrade) => {
//     if (!incomingTrade || !incomingTrade.id) {
//       console.warn('[MEIC Store] upsertTrade called without valid id:', incomingTrade);
//       return;
//     }
//
//     const id = String(incomingTrade.id);
//
//     set((state) => {
//       const prev = state.tradesById[id] || {};
//
//       // Merge the existing trade and the incoming payload.
//       // Incoming fields ALWAYS overwrite existing fields,
//       // so status / PnL / reason from trade_update win over trade_opened.
//       const merged = {
//         ...prev,
//         ...incomingTrade
//       };
//
//       return {
//         tradesById: {
//           ...state.tradesById,
//           [id]: merged
//         }
//       };
//     });
//   },
//
//   /**
//    * 🔹 NEW:
//    * Seed today's trades snapshot from the 'meic_initial_state' WS message.
//    * Payload shape from WS:
//    * {
//    *   type: 'meic_initial_state',
//    *   tradeDate: 'YYYY-MM-DD',
//    *   trades: [ ... rows from DB ... ]
//    * }
//    */
//   setInitialTrades: ({ tradeDate, trades }) => {
//     if (!Array.isArray(trades)) {
//       console.warn('[MEIC Store] setInitialTrades called without valid trades array:', trades);
//       return;
//     }
//
//     // 🔹 DEBUG LOG
//     console.log('[MEIC Store] setInitialTrades snapshot:', {
//       tradeDate,
//       count: trades.length,
//       sample: trades[0] || null,
//     });
//
//     set({
//       dailyTradesDate: tradeDate || null,
//       dailyTrades: trades
//     });
//   },
//
//   /**
//    * Optional helper to clear the daily summary (e.g. on date change).
//    */
//   resetDailySummary: () => {
//     set({ dailySummary: null });
//   },
//
//   /**
//    * Set market state from the 'market_state' WS message.
//    * Keeps marketState and currentRegime in sync.
//    */
//   setMarketState: (state) => {
//     // console.log('[MEIC STORE] Updating marketState:', state);  // 🔹 ADD THIS
//     if (!state || typeof state.regime === 'undefined') {
//       console.warn('[MEIC Store] setMarketState called without valid state:', state);
//       return;
//     }
//     set({
//       marketState: state,
//       currentRegime: state.regime
//     });
//   },
//
//   /**
//    * Optional manual override for regime (if you ever need it).
//    */
//   setRegime: (regime) => {
//     set((prev) => ({
//       currentRegime: regime,
//       marketState: prev.marketState
//         ? { ...prev.marketState, regime }
//         : { regime }
//     }));
//   },
//
//   /**
//    * Set connection status (used by MeicWebsocketClient).
//    * status: 'disconnected' | 'connecting' | 'connected' | 'auth_error'
//    */
//   setConnectionStatus: (status) => {
//     set({ connectionStatus: status });
//   },
//
//   /**
//    * Clear all trades (e.g. when switching accounts, or hard reset).
//    */
//   clearTrades: () => {
//     set({ tradesById: {} });
//   },
//
//   /**
//    * Convenience selectors
//    */
//
//   getAllTrades: () => {
//     const { tradesById } = get();
//     return Object.values(tradesById);
//   },
//
//   getOpenTrades: () => {
//     const { tradesById } = get();
//     return Object.values(tradesById).filter((t) => t.status === 'OPEN');
//   },
//
//   getClosedTrades: () => {
//     const { tradesById } = get();
//     return Object.values(tradesById).filter((t) => t.status === 'CLOSED');
//   },
//
//   getCurrentRegime: () => {
//     const { currentRegime } = get();
//     return currentRegime;
//   },
//
//   getMarketState: () => {
//     const { marketState } = get();
//     return marketState;
//   },
//
//   /**
//    * 🔹 NEW selectors for the DB snapshot
//    */
//   getDailyTrades: () => {
//     const { dailyTrades } = get();
//     return dailyTrades;
//   },
//
//   getDailyTradesDate: () => {
//     const { dailyTradesDate } = get();
//     return dailyTradesDate;
//   }
// }));
//
// export default useMeicWebsocketDataStore;